import L from 'leaflet';
import { InfoCircleOutlined } from '@ant-design/icons';
import 'leaflet/dist/leaflet.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Modal } from 'antd';

// Modal content component for better readability and reusability
const HabitatInfoModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <Modal title='Habitats in Annex I of the Habitats Directive' open={true} onCancel={onClose} footer={null}>
    <p>
      The habitats directive aims to protect species and habitats. The habitats listed in Annex I of this directive are
      referred to as Habitats of Community Interest (HCI). There are manuals for interpreting habitats, at both European
      and French levels. Annex I habitats may justify the creation of a Special Area of Conservation. Special area of
      conservation means « a site of Community importance designated by the Member States through a statutory,
      administrative and/or contractual act where the necessary conservation measures are applied for the maintenance or
      restoration, at a favourable conservation status, of the natural habitats and/or the populations of the species
      for which the site is designated » (see Council Directive 92/43/EEC). The Natura 2000 network is composed of
      Special Areas of Conservation and Special Protection Areas classified by the Member States in accordance with
      Directive 79/409/EEC.
    </p>
    <p>
      <b>Extract from article 3 of the habitats directive</b>
      <br />« A coherent European ecological network of special areas of conservation shall be set up under the title
      Natura 2000. This network, composed of sites hosting the natural habitat types listed in Annex I and habitats of
      the species listed in Annex II, shall enable the natural habitat types and the species' habitats concerned to be
      maintained or, where appropriate, restored at a favourable conservation status in their natural range » (see
      Council Directive 92/43/EEC).
    </p>
    <p>
      <b>Summary of article 6 paragraphs 1 to 4</b>
      <br />
      The Member States undertake, in particular, to: (1) establish conservation measures in Special Areas of
      Conservation that meet the ecological requirements of the natural habitat types listed in Annex I and the habitats
      of species listed in Annex II; (2) avoid the deterioration of natural habitats in Special Areas of Conservation;
      (3) approve a project on the site of a Special Area of Conservation if it does not adversely affect the
      conservation objectives of the site and (4) implement a compensatory measure if there are no alternative solutions
      to the project and if the project must be carried out for reasons of major public interest. To find out more about
      Natura 2000, click{' '}
      <a href='https://www.natura2000.fr/natura-2000/qu-est-ce-que-natura-2000' target='_blank'>
        here
      </a>
      .
    </p>
  </Modal>
);

class FranceHabitatsControl extends L.Control {
  private map: L.Map | null = null;
  private modalRoot: ReactDOM.Root | null = null;

  constructor(options?: L.ControlOptions) {
    super({ position: 'bottomright', ...options });
  }

  onAdd(map: L.Map) {
    this.map = map;

    // Create the container button
    const div = L.DomUtil.create('div', 'france-control-info');
    div.id = 'MapCustomFranceHabitatsId';
    Object.assign(div.style, {
      position: 'absolute',
      bottom: '10px',
      right: '0px', // Adjusted for spacing
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
      zIndex: '1000',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
    });

    // Render the Ant Design icon inside the button
    const reactContainer = document.createElement('div');
    div.appendChild(reactContainer);
    ReactDOM.createRoot(reactContainer).render(<InfoCircleOutlined style={{ fontSize: '16px', color: '#1890ff' }} />);

    // Click event listener for showing the modal
    div.addEventListener('click', this.showInfoModal.bind(this));

    return div;
  }

  onRemove() {
    this.map = null;
    this.cleanupModal();
  }

  private showInfoModal() {
    if (!this.modalRoot) {
      const modalContainer = document.createElement('div');
      document.body.appendChild(modalContainer);
      this.modalRoot = ReactDOM.createRoot(modalContainer);
    }

    this.modalRoot.render(<HabitatInfoModal onClose={this.cleanupModal.bind(this)} />);
  }

  private cleanupModal() {
    if (this.modalRoot) {
      this.modalRoot.unmount();
      this.modalRoot = null;
    }
  }
}

export default FranceHabitatsControl;
